import { GridViewMetadata } from 'apps/webcomponents/src/grid/gridView/types';
import { ColumnDetails, ColumnType, CompanyColumnDetails } from 'apps/webcomponents/src/grid/types/common';

import {
  Column,
  ColumnKeys,
  Dependencies,
  ExchangeProtectionWebComponentSchema,
  Permissions,
  getTranslatedQuarantineActionStatusFilterValues,
  getTranslatedQuarantineActionStatusToName,
} from '../types';
import moment from 'moment';
import { ExchangeProtectionGridDataProvider } from '../dataProvider/ExchangeProtectionGridDataProvider';
import { convertIntToThreatsStatus, statusCodesFilterValues } from '../types/statusCodes';
import { Filter } from 'apps/webcomponents/src/grid/gridFilters/types';
import { html } from 'lit';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { RELATIVE_TIME_RANGES } from '@nirvana/shared/constants';
import {SelectableAction} from "../../computersAndVirtualMachines/types";
import {SelectOption} from "../../../ui/gzSelect/types";

export class GridConfig {
  private companyFiltersValues;
  private companyFilters;

  private columnRenderers = {
    [ColumnKeys.SUBJECT]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${rowData?.item?.email?.subject}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.SENDER]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${rowData?.item?.email?.senderEmail}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.RECIPIENTS]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${rowData?.item?.email?.recipients}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.REAL_RECIPIENTS]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${rowData?.item?.email?.realRecipients}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.STATUS]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${convertIntToThreatsStatus(rowData?.item?.threats?.status)}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.MALWARE_NAME]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${rowData?.item?.threats?.name}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.SERVER_NAME]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${rowData?.item?.source?.protectedEntity?.name}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.COMPANY]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${rowData?.item?.source?.company?.name}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.QUARANTINED]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${moment(rowData?.item?.quarantinedDate).format('DD MMM YYYY, HH:mm')}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.ACTION_STATUS]: rowData => {
      const QuarantineActionStatusToName = getTranslatedQuarantineActionStatusToName();

      return html`<gz-grid-cell-default-renderer
        .label="${QuarantineActionStatusToName[rowData?.item?.actionStatus]}"
      ></gz-grid-cell-default-renderer>`;
    },
  };

  private pageSizeOptions = [
    {
      title: '20',
      value: '20',
    },
    {
      title: '50',
      value: '50',
    },
    {
      title: '100',
      value: '100',
    },
  ];

  private filtersUiConfig = [
    {
      key: ColumnKeys.QUARANTINED,
      config: {
        activeRange: null,
      },
    },
  ];

  getDataForView(dependencies: Dependencies, companyFilters): ExchangeProtectionWebComponentSchema {
    const { permissions, translateFn } = dependencies;
    this.companyFilters = companyFilters;
    let companyFilter = dependencies.getCurrentUserCompany().id;
    if(Application.Main.getUser()['mdrSuperAnalyst']) {
      companyFilter = dependencies.getActiveCompany().companyId
    }

    const viewMetaData: GridViewMetadata<Column, ColumnKeys> = {
      viewId: 'quarantine-exchange-protection',
      filters: this.getDefaultFilters(permissions, this.companyFilters, companyFilter),
      predefinedFiltersKeys: this.getPredefinedFiltersKeys(permissions),
      columns: this.getColumns(),
      pageSize: 100,
      isCompactViewChecked: false,
      hasSelectableRows: true,
      hasPagination: true,
      pageSizeOptions: this.pageSizeOptions,
      columnRenderers: this.columnRenderers,
      showGlobalActions: true,
      columnConfigs: [],
      filtersUiConfigs: this.filtersUiConfig,
      allColumnDetails: this.getColumnDetails(translateFn, permissions, companyFilter),
      pageNumber: 1,
      dataProvider: new ExchangeProtectionGridDataProvider(dependencies),
      enableCSVExport: false,
      sortColumnName: ColumnKeys.COMPANY,
      sortDirection: 'DESC',
      cellClassNameGenerator: this.getCellClassNameGenerator,
    };

    return {
      viewMetaData,
      actions: this.getActions(dependencies.translateFn),
    };
  }

  private getColumns(): Column[] {
    return [
      Column.SUBJECT,
      Column.SENDER,
      Column.RECIPIENTS,
      Column.REAL_RECIPIENTS,
      Column.STATUS,
      Column.MALWARE_NAME,
      Column.COMPANY,
      Column.QUARANTINED,
      Column.ACTION_STATUS,
    ];
  }

  private getDefaultFilters(permissions: Permissions, companyFilters, companyId: string): Array<Filter<ColumnKeys>> {
    this.companyFiltersValues = {};

    companyFilters?.forEach(company => {
      this.companyFiltersValues[company._id] = company.name;
    });

    let filters: Array<Filter<ColumnKeys>> = [
      {
        [ColumnKeys.QUARANTINED]: {
          timeRange: {
            kind: TimeRangeKind.RELATIVE_TIME_RANGE,
            relative: RELATIVE_TIME_RANGES.LAST_30_DAYS,
          },
        },
      },
      {
        [ColumnKeys.ACTION_STATUS]: {
          value: '', //Object.values(QuarantineActionStatus),
        },
      },
    ];

    if (permissions.canHaveChildCompanies) {
      filters = [
        {
          [ColumnKeys.COMPANY]: {
            in: [companyId],
          },
        },
        ...filters,
      ];
    }

    return filters;
  }

  private getPredefinedFiltersKeys(permissions: Permissions): Array<ColumnKeys> {
    let keys = [ColumnKeys.QUARANTINED, ColumnKeys.ACTION_STATUS];

    if (permissions.canHaveChildCompanies) {
      keys = [ColumnKeys.COMPANY, ...keys];
    }

    return keys;
  }

  private getColumnDetails(
    translateFn: Function,
    permissions: Permissions,
    companyId: string
  ): Array<ColumnDetails<Column, ColumnKeys>> {
    const columnDetails: Array<ColumnDetails<Column, ColumnKeys>> = [
      {
        column: Column.SUBJECT,
        key: ColumnKeys.SUBJECT,
        columnName: translateFn('Subject'),
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
      },
      {
        column: Column.SENDER,
        key: ColumnKeys.SENDER,
        columnName: translateFn('Sender'),
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
      },
      {
        column: Column.RECIPIENTS,
        key: ColumnKeys.RECIPIENTS,
        columnName: translateFn('Recipients'),
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
      },
      {
        column: Column.REAL_RECIPIENTS,
        key: ColumnKeys.REAL_RECIPIENTS,
        columnName: translateFn('Real recipients'),
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
      },
      {
        column: Column.STATUS,
        key: ColumnKeys.STATUS,
        columnName: translateFn('Status'),
        type: ColumnType.SELECT,
        values: statusCodesFilterValues,
        removable: true,
        sortable: true,
      },
      {
        column: Column.MALWARE_NAME,
        key: ColumnKeys.MALWARE_NAME,
        columnName: translateFn('Threat name'),
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
      },
      {
        column: Column.SERVER_NAME,
        key: ColumnKeys.SERVER_NAME,
        columnName: translateFn('Server name'),
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
      },
      {
        column: Column.QUARANTINED,
        key: ColumnKeys.QUARANTINED,
        columnName: translateFn('Quarantined on'),
        type: ColumnType.DATE,
        removable: true,
        sortable: true,
        filterResetValue: {
          timeRange: {
            kind: TimeRangeKind.RELATIVE_TIME_RANGE,
            relative: RELATIVE_TIME_RANGES.LAST_30_DAYS,
          },
        },
      },
      {
        column: Column.ACTION_STATUS,
        key: ColumnKeys.ACTION_STATUS,
        columnName: translateFn('Action status'),
        type: ColumnType.SELECT,
        values: getTranslatedQuarantineActionStatusFilterValues(),
        removable: true,
        sortable: true,
      },
    ];

    if (permissions.canHaveChildCompanies) {
      const indexToInsert = 7;
      const deleteCount = 0;
      const columnToInsert: CompanyColumnDetails<Column, ColumnKeys> = {
        column: Column.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: translateFn('Company'),
        type: ColumnType.COMPANY,
        values: this.companyFilters?.map(company => ({ value: company._id, label: company.name })),
        removable: false,
        sortable: true,
        defaultTarget: [],
        filterResetValue: [companyId],
      };

      columnDetails.splice(indexToInsert, deleteCount, columnToInsert);
    }

    return columnDetails;
  }

  private getActions(translateFn: any): Array<SelectOption> {
    return [
      {
        title: translateFn('Release as attachment'),
        value: SelectableAction.RESTORE,
        disabled: true,
        id: 'restore',
      },
      {
        title: translateFn('Release to intended recipient'),
        value: SelectableAction.RESTORE_ORIGINAL,
        disabled: true,
        id: 'restoreOriginal',
      },
    ];
  }

  private getCellClassNameGenerator(_, rowData): string {
    const {
      item: { isGhost },
    } = rowData;

    return isGhost ? 'cell-ghost' : '';
  }
}
